// src/pages/Tenants_List.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  Plus,
  Edit3,
  Loader2,
  Database,
} from "lucide-react";

const Tenants_List = () => {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState(null); // row toggle in-flight

  // pagination
  const [page, setPage] = useState(1);
  const perPage = 10;
  const first = (page - 1) * perPage;
  const current = filtered.slice(first, first + perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get("https://superadmin.nissiindia.com/api/tenants");
      const data = Array.isArray(r.data) ? r.data : [];
      setRows(data);
      setFiltered(data);
      setPage(1);
    } catch (e) {
      console.error("Failed to load tenants:", e);
      Swal.fire("Error", "Unable to load tenants.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearch(term);
    if (!term) {
      setFiltered(rows);
      setPage(1);
      return;
    }
    const f = rows.filter((x) =>
      String(x.TenantCode || "").toLowerCase().includes(term) ||
      String(x.Name || "").toLowerCase().includes(term) ||
      String(x.DatabaseName || "").toLowerCase().includes(term)
    );
    setFiltered(f);
    setPage(1);
  };

  const toggleSuspend = async (tenantId) => {
    try {
      setPendingId(tenantId);
      await axios.post("https://superadmin.nissiindia.com/api/license/toggle-suspend", {
        tenantId,
        actor: "ui",
      });
      await load();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Toggle failed.", "error");
    } finally {
      setPendingId(null);
    }
  };

  const goEdit = (row) => {
    navigate(`/superAdmin/tenantEdit/${row.TenantId}`, { state: { row } });
  };

  const badgeClass = (status) => {
    if (status === "ACTIVE")
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    if (status === "BLOCKED")
      return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
    return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
  };

  // True if tenant is currently suspended
  const isSuspended = (row) =>
    row.EffectiveStatus === "BLOCKED" || row.IsSuspended === true;

  // Determine product label from DB name
const getProductLabel = (row) => {
  const db = String(row.DatabaseName || "").toLowerCase();

  if (db.startsWith("patrolv_t_")) return "Patrol Visit";
  if (db.startsWith("patrol_t_")) return "EBD Patrol";
  if (db.startsWith("sentinel_t_")) return "Sentinel";

  return "Unknown";
};

  // Toggle component (accessible, no external CSS)
  const SuspendToggle = ({ row }) => {
    const checked = !isSuspended(row); // ON means not suspended
    const disabled = pendingId === row.TenantId;

    return (
      <label className="relative inline-flex items-center cursor-pointer select-none">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          disabled={disabled}
          onChange={() => toggleSuspend(row.TenantId)}
          aria-label={checked ? "Set Suspended" : "Set Active"}
        />
        {/* Track */}
        <div
          className={`h-6 w-12 rounded-full transition-colors duration-200
            ${disabled ? "opacity-70" : ""}
            ${checked ? "bg-emerald-500" : "bg-rose-500"}
            peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-slate-300`}
        />
        {/* Thumb */}
        <div
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200
            ${checked ? "translate-x-6" : ""}`}
        />
        {/* Status text */}
        <span
          className={`ml-2 text-xs font-medium ${
            checked ? "text-emerald-700" : "text-rose-700"
          }`}
        >
          {disabled ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…
            </span>
          ) : checked ? (
            "Active"
          ) : (
            "Suspended"
          )}
        </span>
      </label>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Stack on mobile, row on md+ */}
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        {/* Sidebar (mobile drawer handled inside component; 25% on md+) */}
        <SuperAdminNav />

        {/* Main (full on mobile, 75% on md+) */}
        <main className="w-full md:w-3/4 md:h-full md:overflow-auto py-6 px-4 sm:px-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Tenants</h2>
                <p className="text-sm text-slate-500">Manage all customer instances</p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              <Time />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <Link to={`/superAdmin/tenant_Add`}>
              <button className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                <Plus className="h-4 w-4" />
                Add New
              </button>
            </Link>

            <div className="relative w-full sm:w-96">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={onSearch}
                placeholder="Search by code / name / db"
                className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow">
            <table className="min-w-full text-sm">
              {/* Header with distinct color */}
              <thead className="bg-gradient-to-r from-slate-900 to-slate-800 text-slate-100">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">#</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Code</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Name</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">DB</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Product</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Status</th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Suspend</th>
                </tr>
              </thead>

              {/* Zebra striping for td */}
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center">
                      <span className="inline-flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                      </span>
                    </td>
                  </tr>
                ) : current.length > 0 ? (
                  current.map((r, i) => (
                    <tr
                      key={r.TenantId}
                      className="odd:bg-white even:bg-slate-50 hover:bg-slate-100/60"
                    >
                      <td className="px-4 py-3 text-slate-500">{first + i + 1}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium">{r.TenantCode}</td>
                      <td className="px-4 py-3 text-slate-900">{r.Name}</td>
                      <td className="px-4 py-3 text-slate-700">{r.DatabaseName}</td>
                      <td className="px-4 py-3">
                        {(() => {
  const label = getProductLabel(r);

  // Product-specific badge colors
 const productClass =
  label === "EBD Patrol"
    ? "bg-indigo-50 text-indigo-700 ring-indigo-200"
    : label === "Patrol Visit"
    ? "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200"
    : label === "Sentinel"
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : "bg-slate-100 text-slate-700 ring-slate-200";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${productClass}`}
    >
      {label}
    </span>
  );
})()}

                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${badgeClass(
                            r.EffectiveStatus
                          )}`}
                        >
                          {r.EffectiveStatus ?? "-"}
                        </span>
                      </td>

                      {/* Toggle suspend/unsuspend */}
                      <td className="px-4 py-3">
                        <SuspendToggle row={r} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center text-slate-500">
                      No Tenants
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};


export default Tenants_List;
